import { isDef, isObject } from "./is";
import { RewardTypeEnum } from "../../../apps/h5-client/livevoice-business/src/types/enum";
// 引入公共hooks
import { useFormatDate } from "../../hooks/src/use-format-date";

/**
 * 空函数
 * @returns {void}
 */
export function noop() {}

/**
 * 获取对象中的某一个路径值
 * @param {unknown} object - 对象
 * @param {String} path - 路径
 * @returns {unknown} 路径值
 * @example import { get } from "@ht/utils";
 * get({a:{b:1}}, "a.b");
 * // 输出1
 */
export function get(object: unknown, path: string): unknown {
  const keys = path.split(".");
  let result = object;

  keys.forEach(key => {
    result = isObject(result) ? result[key] ?? "" : "";
  });

  return result;
}

/**
 * 转换为数组
 * @param {T|T[]} item - 值
 * @returns {T[]} 数组
 * @example import { toArray } from "@ht/utils";
 * toArray(1);
 * // 输出[1]
 */
export const toArray = <T>(item: T | T[]): T[] =>
  Array.isArray(item) ? item : [item];

/**
 * 对象深拷贝
 * @param {Record<string, unknown> | null | undefined} obj - 对象
 * @returns {Record<string, unknown> | null | undefined} 拷贝后的对象
 * @example import { deepClone } from "@ht/utils";
 * deepClone({a:1});
 */
export function deepClone<
  T extends Record<string, unknown> | null | undefined | object
>(obj: T): T {
  if (!isDef(obj)) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => deepClone(item)) as unknown as T;
  }

  if (isObject(obj)) {
    const to = {} as Record<string, unknown>;
    Object.keys(obj).forEach(key => {
      to[key] = deepClone(
        obj[key] as Record<string, unknown> | null | undefined
      );
    });

    return to as T;
  }

  return obj;
}

/**
 * 节流函数
 * @param {Function}fn
 * @param {number} delay
 * @returns
 */
export const useThrottle = (fn: () => void, delay: number) => {
  let lastTime = 0;
  let isFirstRun = true;
  return (...args: []) => {
    const now = Date.now();
    if (isFirstRun || now - lastTime > delay) {
      isFirstRun = false;
      lastTime = now;
      return fn(...args);
    }
  };
};

/**
 * 生成倒计时时间
 * @param {number} currentTimestamp  当前时间戳
 * @param {number} deadlineTimestamp  截止时间戳
 * @returns {Array<string>}
 */
export const calculateCountdownTime = (
  currentTimestamp: number,
  deadlineTimestamp: number
) => {
  // 计算差值（单位：毫秒）
  const diff = deadlineTimestamp - currentTimestamp;

  // 检查倒计时是否已经结束
  if (diff <= 0) {
    return { days: "00", hours: "00", minutes: "00", seconds: "00" };
  }

  // 计算各个单位的时间
  const days = String(Math.floor(diff / (1000 * 60 * 60 * 24))).padStart(
    2,
    "0"
  );
  const hours = String(
    Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  ).padStart(2, "0");
  const minutes = String(
    Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  ).padStart(2, "0");
  const seconds = String(Math.floor((diff % (1000 * 60)) / 1000)).padStart(
    2,
    "0"
  );

  // 返回格式化的字符串
  return { days, hours, minutes, seconds };
};

/**
 * @description 生成一个获取链接参数的方法
 * @returns {object}
 */
export const getAllQueryParams = () => {
  const urlObj = new URL(window.location.href);
  const params: Record<string, string> = {};

  urlObj.searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
};

/**
 * @description 复制文字，支持换行
 * @param {string} copyText - 复制的文字
 * @returns {void}
 */
export const copyBranchText = (copyText: string) => {
  const textarea = document.createElement("textarea");
  textarea.value = copyText;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

/**
 * @description 根据礼物类型返回对应数量显示
 * @param {RewardTypeEnum} giftType - 礼物类型
 * @param {number} giftCount - 礼物数量
 * @returns {string} 礼物数量显示
 */
export const getGiftCount = (giftType: number, giftCount: number) => {
  if (
    [
      RewardTypeEnum.ReceiveTypeRipple,
      RewardTypeEnum.ReceiveTypeBubble,
      RewardTypeEnum.ReceiveTypeRoomBackground,
      RewardTypeEnum.ReceiveTypeDataFrame,
      RewardTypeEnum.ReceiveTypeGiftStreamer
    ].includes(giftType)
  ) {
    // 声波、气泡、房间背景、资料边框
    return `* ${giftCount}d`;
  }
  return `* ${giftCount}`;
};

/**
 * @description 语种日期格式化
 * @param {string} nation 国籍
 * @param {number} timestamp 时间戳
 */
export const multilingualDateFormat = (nation: string, timestamp: number) => {
  const { formatWidthInternationalDate } = useFormatDate({
    country: nation,
    exactly: true
  });
  return formatWidthInternationalDate(timestamp);
};
